import React from "react";
import { Typography, tokens } from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import type { DesignPreferenceItemFormatted } from "../../types/designPreferences";
import { DesignPreferenceField } from "../atoms/DesignPreferenceField";
import { getFieldKey } from "../../utils/groupDesignPreferences";
import {
  getDesignPreferenceValue,
  isEffectivelyRequired,
  shouldShowDesignPreferenceField,
} from "../../utils/designPreferenceConditions";
import { mobileMaxWidth } from "../../../components/atoms/GlobalStyles";
import { ViewMode } from "checkout/types/payments-ui-form";

type DesignPreferenceSectionProps = {
  /** All fields in this card (flattened sections) so parent/child links resolve across subsections. */
  allFieldsInGroup: DesignPreferenceItemFormatted[];
  subHeader?: DesignPreferenceItemFormatted;
  fields: DesignPreferenceItemFormatted[];
  viewMode: ViewMode;
  formValues: Map<string, string>;
  onFieldChange: (
    item: DesignPreferenceItemFormatted,
    index: number,
    value: string
  ) => void;
  fieldIndices: Map<DesignPreferenceItemFormatted, number>;
};

function isTextInputField(f: DesignPreferenceItemFormatted) {
  return f.respDataType === "Text" || f.respDataType === "Phone";
}

/**
 * Finds groups of consecutive text input fields in the fields array.
 * This is used for grid layout purposes - when we have an odd number (3+) of
 * consecutive text inputs, we add a spacer to keep the grid balanced.
 */
function findConsecutiveTextInputGroups(
  fields: DesignPreferenceItemFormatted[]
): { startIndex: number; count: number }[] {
  const groups: { startIndex: number; count: number }[] = [];
  let currentIndex = 0;

  while (currentIndex < fields.length) {
    if (!isTextInputField(fields[currentIndex])) {
      currentIndex++;
      continue;
    }

    let consecutiveCount = 0;
    while (
      currentIndex + consecutiveCount < fields.length &&
      isTextInputField(fields[currentIndex + consecutiveCount])
    ) {
      consecutiveCount++;
    }

    groups.push({ startIndex: currentIndex, count: consecutiveCount });
    currentIndex += consecutiveCount;
  }

  return groups;
}

const DesignPreferenceSection: React.FC<DesignPreferenceSectionProps> = ({
  allFieldsInGroup,
  subHeader,
  fields,
  viewMode,
  formValues,
  onFieldChange,
  fieldIndices,
}) => {
  if (fields.length === 0 && !subHeader) {
    return null;
  }

  const getValue = (item: DesignPreferenceItemFormatted): string =>
    getDesignPreferenceValue(item, viewMode, fieldIndices, formValues);

  const shouldShowField = (field: DesignPreferenceItemFormatted): boolean =>
    shouldShowDesignPreferenceField(field, allFieldsInGroup, getValue);

  const textInputGroups = findConsecutiveTextInputGroups(fields);

  const shouldAddSpacerAfter = (index: number) => {
    const group = textInputGroups.find(
      (g) => g.startIndex <= index && index < g.startIndex + g.count
    );

    if (!group || group.startIndex !== index) return false;
    return group.count >= 3 && group.count % 2 === 1;
  };

  return (
    <>
      {subHeader && (
        <StyledSubtitleTypography size={18} fontWeight="medium">
          {subHeader.question}
        </StyledSubtitleTypography>
      )}
      {fields.length > 0 && (
        <InfoFields>
          {fields.flatMap((field, index) => {
            if (!shouldShowField(field)) {
              return [];
            }
            if (viewMode === "view" && field.relatedURL) {
              return [];
            }

            const originalIndex = fieldIndices.get(field) ?? index;
            const fieldKey = getFieldKey(field, originalIndex);
            const isTextInput = isTextInputField(field);
            const nodes: React.ReactElement[] = [
              <InfoFieldWrapper
                key={index}
                data-text-input={isTextInput ? "true" : undefined}
              >
                <DesignPreferenceField
                  item={field}
                  viewMode={viewMode}
                  value={formValues.get(fieldKey) || ""}
                  onChange={(value) =>
                    onFieldChange(field, originalIndex, value)
                  }
                  fieldIndex={originalIndex}
                  showRequiredAsterisk={isEffectivelyRequired(
                    field,
                    allFieldsInGroup,
                    getValue
                  )}
                />
              </InfoFieldWrapper>,
            ];
            if (shouldAddSpacerAfter(index)) {
              nodes.push(
                <GridSpacer
                  key={`spacer-${index}`}
                  data-grid-spacer
                  aria-hidden
                />
              );
            }
            return nodes;
          })}
        </InfoFields>
      )}
    </>
  );
};

const StyledSubtitleTypography = styled(Typography)`
  color: ${tokens.BRAND_HEROBLUE};
`;

const InfoFields = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;

  & > * {
    grid-column: 1 / -1;
    min-width: 0;
  }

  & > [data-text-input="true"],
  & > [data-grid-spacer] {
    grid-column: span 1;
  }

  @media (max-width: ${mobileMaxWidth}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const InfoFieldWrapper = styled.div`
  min-width: 0;
`;

const GridSpacer = styled.div``;

export { DesignPreferenceSection };
